<script setup>
import { ref, computed, watch } from 'vue'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { ArrowLeft } from "lucide-vue-next"
import {bus, validateProjectDuration, isBookingInProject, cleanx, popupProjectsList, getResourceRange, is_bookable} from "./planner.js";
import {toIso, toGerman, fromGerman} from '@/utils/dateUtils';
import DateInput from "./DateInput.vue"
import Personlist from "./Personlist.vue"

defineExpose({
  handleSelectionComplete,
  handleSelectionClose,
})

const emit = defineEmits(['bookingDone', 'dateupdate', 'close'])

const props = defineProps({projects: { type: Array, required: true },
                           users: { type: Array, required: true },
						   bookings: { type: Array, required: true },
                           xScale: {type: Function, required: true},
						   days: { type: Array, required: true}});

// selectedDays: ISO-Tage ("yyyy-mm-dd"), aufsteigend.
const selectedDays      = ref([]);
const selectedResources = ref([]);
const selectedPid       = ref(null);
const startDate = ref("31-12-1964");
const endDate = ref("31-12-1964");

let freeup = { id: -1, name: 'Freimachen', color: 'lightgray' }

function handleDateUpdate(start, end) {
	selectedDays.value = [fromGerman(start), fromGerman(end)];
	emit('dateupdate', start, end);
}

function handleDateUpdateStart(start) {
	startDate.value = start
	handleDateUpdate(start, endDate.value);
}

function handleDateUpdateEnd(end) {
	endDate.value = end;
	handleDateUpdate(startDate.value, endDate.value);
}

const showPopup          = ref(false);

// Guard gegen "programmatisch ausblenden" != "Nutzer hat verworfen": Beim Ziehen
// an den Griffen blendet der Planer das Sheet kurz weg. Ohne diese Unterscheidung
// verstand der Watcher unten das als Verwerfen und der Parent löschte das
// Auswahl-Rechteck – man konnte die Auswahl also nie nachjustieren.
// Gleiches Muster wie suppressSync in D3Planner.
let suppressCloseEmit = false;

// Schließt sich das Sheet (z.B. durch Tippen/Klicken außerhalb), muss auch die
// blaue Auswahl im Planer geräumt werden – das übernimmt der Parent via closeSelection().
watch(showPopup, (open) => {
  if (open) return
  if (suppressCloseEmit) { suppressCloseEmit = false; return }
  emit('close')
})
const containsBookings   = ref(false);
const showBookingOptions = ref(false);

const orientation = computed(() =>
  window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
)

let projects = ref([]);

// Called when selection box is completed
function handleSelectionComplete(b) {
  selectedDays.value = computeSelectedDays(b);
  if (selectedDays.value.length <= 0) {
	return;
  }

  let firstDay = selectedDays.value[0];
  let lastDay  = selectedDays.value.at(-1);

  startDate.value = toGerman(firstDay);
  endDate.value = toGerman(lastDay);
  projects.value = popupProjectsList(props.projects).filter(p => isBookingInProject(p, {start: firstDay, end: lastDay}));

  const timesheet = document.getElementById("timesheet");
  let [selected_resources, selected_pid] = getResourceRange(b, props.users, timesheet);
  console.log("selected_resources=" + JSON.stringify(selected_resources.map(u => (u.id + ":" + u.short))));
  console.log("selected_pid=" + selected_pid);

  if (selected_resources == null || selected_pid == null) {
	return
  }
  selectedResources.value = selected_resources;
  selectedPid.value = selected_pid;
  containsBookings.value = props.bookings.some(b => selected_resources.some(u => u.id === b.user_id) && is_bookable(b, selectedDays.value));
  console.log("containsBookings.value=" + containsBookings.value);
  showPopup.value = true
}

// Blendet das Sheet aus, ohne die Auswahl im Planer zu verwerfen.
function handleSelectionClose() {
  selectedResources.value = [];
  // Flag nur setzen, wenn der Watcher gleich auch wirklich feuert – sonst bliebe
  // es stehen und würde das nächste echte Schließen verschlucken.
  if (showPopup.value) {
    suppressCloseEmit = true;
    showPopup.value = false;
  }
}

// Auswahl-Rechteck -> ISO-Tage. Ein Tag gehoert dazu, wenn seine Zellmitte in
// der Box liegt - das ist gegen Rundung unempfindlich und braucht keine
// Sonderfaelle an den Raendern. Der fruehere Filter `> x1`/`<= x2` war die
// Kompensation der lokalen Achse und ist mit TIM-36 entfallen.
function computeSelectedDays(box) {
  const [x1, x2] = [box.x, box.x + box.width].sort((a, b) => a - b)

  // Zellbreite aus dem Raster ableiten statt sie zu uebergeben.
  const days = props.days
  const breite = days.length > 1
    ? cleanx(props.xScale, days[1]) - cleanx(props.xScale, days[0])
    : 0

  return days
    .filter(day => {
      const mitte = cleanx(props.xScale, day) + breite / 2;
      return mitte > x1 && mitte < x2
    })
    .map(toIso)
}

async function selectAction(option) {
	
  let action = option.id;
  
  let sdays = selectedDays.value;
  let firstDay = sdays.at(0);
  let lastDay  = sdays.at(-1);

  let validate = false;
  let extend = false;
  let newpstart = null;
  let newpend = null;

  if (action >= 0) {
	let project = props.projects.find(p => p.id === parseInt(selectedPid.value));
	[validate, extend, newpstart, newpend] = validateProjectDuration(project, firstDay, lastDay);
	if (!validate) {
		return;
	}
  }

  const params = {
	booking_action: action,
	selected_pid: parseInt(selectedPid.value), 
	firstDay: firstDay,
	lastDay: lastDay,
	newpstart: extend ? newpstart : null,
	newpend: extend ? newpend : null,
	selectedResources: selectedResources.value.map(u => u.id),
  }
  showPopup.value = false
  showBookingOptions.value = false
  emit('bookingDone', params);
  
  if (action >= 0) {
    bus.emit('project-updated', params);
  }
}

function bookProject(val) {
  showBookingOptions.value = val;
}

const selectedOption = ref(null);
</script>

<template>
	<!-- Bewusst unmodal: waehrend das Auswahl-Popup offen ist, muss der Planer
	     dahinter bedienbar bleiben - man zieht an den Griffen der Auswahl und
	     sieht das Ergebnis sofort. Modal legte die Seite dahinter still.
	     Frueher ergab sich das aus einem Fehler in den ui-Wrappern (modal kam
	     als false an, obwohl reka-ui true vorgibt); seit das behoben ist, muss
	     es hier stehen. -->
	<Sheet class="test" v-model:open="showPopup" :modal="false">
	  <SheetContent :side="orientation === 'landscape' ? 'right' : 'bottom'" class="booking-select p-4 w-64 max-w-full overflow-y-auto" style="z-Index: 10000 !important">
		<div v-if="!showBookingOptions">
		  <h3 v-if="containsBookings"  class="text-lg font-semibold mb-2">Was möchten Sie mit dem ausgewählten Bereich tun?</h3>
	      <h3 v-if="!containsBookings" class="text-lg font-semibold mb-2">Neue Buchung in {{props.projects.find(p => p.id === selectedPid).name}}</h3>
		  <Personlist label="Personen:" :words="selectedResources.map(r => r.short)" />
		  <div class="spacer"/>
		  
		  <!-- A dummy input box so that when the component opens up in Safari -->
		  <!-- it does not automatically open up the date picker!! Impossible to quell any other way -->
		  <!-- the first focal point of the browser will be the dummy -->
		  <input style="display: none;" />
		  <!-- end of hack -->
		  
		  <DateInput label="Start" :modelValue="startDate" :max="endDate" @datechange="handleDateUpdateStart" />
		  <div class="spacer"/>
		  <DateInput label="Ende"  :modelValue="endDate" :min="startDate" @datechange="handleDateUpdateEnd" />

		  <div class="spacer"/>

		  <button v-if="containsBookings"
		    class="w-full flex items-center space-x-2 text-center timin_text_center px-3 py-2 bg-timin hover:brightness-90 text-black"
		    @click="selectAction(freeup)"
	      >
		    <span>{{ freeup.name }}</span>
		  </button>

		  <div class="spacer"/>

		  <button 
		    class="w-full flex items-center space-x-2 text-center timin_text_center px-3 py-2 bg-timin hover:brightness-90 text-black"
		    @click="bookProject(true)"
			style="font-size: 97%;"
		  >
		    <span>In anderem Projekt buchen</span>
		  </button>

		  <div class="spacer"/>

		  <button
		    class="w-full flex items-center space-x-2 text-center timin_text_center px-3 py-2 bg-timin hover:brightness-90 text-black"
		    @click="selectAction({id: selectedPid})"
		  >
		    <span>Im Projekt buchen</span>
		  </button>

		</div>
		<div v-if="showBookingOptions">
          <Button variant="outline" class="flex items-center gap-2"
			 @click="bookProject(false)">
			<ArrowLeft class="w-4 h-4" />
		  </Button>
		  <h3 class="text-lg font-semibold mb-2">In welches Projekt möchten Sie die Buchungen verschieben?</h3>

		  <div class="flex flex-col space-y-4 w-64">
			<RadioGroup v-model="selectedOption" class="flex flex-col space-y-3">
			<div
				v-for="option in projects.filter(p => is_bookable(p, selectedDays))"
				:key="option.id"
				class="flex items-center space-x-2"
			>
				<RadioGroupItem
				:id="`project-${option.id}`"
				:value="option.id"
				/>
				<Label
				:for="`project-${option.id}`"
				:style="{
					backgroundColor: option.color,
					color: 'black',
					height: '25px',
					display: 'flex',
					flex: 0.5,
					alignItems: 'center',
					paddingLeft: '10px'
				}"
				>
				{{ option.name }}
				</Label>
			</div>
			</RadioGroup>
			<Button @click="selectAction({id: selectedOption})" :disabled="selectedOption == null">
			  Verschieben
			</Button>
		  </div>

	  </div>
	  </SheetContent>
	</Sheet>
</template>

<style scoped>
.timin_text_center {
	justify-content: center;
}
</style>